// @givmo/app-charity — the payout changes' words in the shared "confirm it's you" prompt
// (CCM-R1-STEP-UP). The bank-details change speaks the approved frames' words (D-AS-21's B2c and
// B3a–B3c, ruled for AS-WEB10): the 30-minute sign-in check (the backend's
// `STEP_UP_AUTH_WINDOW_SECONDS`, 1800) and what is saved when, with "Nothing is saved until you
// confirm." at the sheet's foot. The check election, which no frame drew, keeps AS-WEB3's line. The
// title and buttons are the package's ("Confirm it's you"; "Confirm", or "Continue" when a code
// follows), and an action the backend names without a row here reads the fallback.
//
// ⚠ THE ROWS ARE KEYED BY THE ACTION THE BACKEND'S REFUSAL NAMES (`context.action`), which is what
// picks a row: the scopes `@require_recent_auth` gives `CharityPaymentService.submit_ach` and
// `elect_check` (`charity_payout_ach`, `charity_payout_check`; the backend pins them in
// `tests/application/test_step_up.py`). Keyed by the operations' own names, as they were until
// cut 1025b's review (W1), no refusal ever found its row and every payout prompt read the fallback.

import type { StepUpCopyTable } from '@givmo/step-up';

/** The bank-details save's step-up action, as its refusal names it. */
export const PAYOUT_ACH_STEP_UP_ACTION = 'charity_payout_ach';
/** The check election's step-up action, as its refusal names it. */
export const PAYOUT_CHECK_STEP_UP_ACTION = 'charity_payout_check';

/** What the person was doing when the backend asked them to re-confirm, in AS-WEB3's words. */
function confirmBefore(what: string): string {
  return `For your security, confirm your identity ${what}. Your details are saved as soon as you do — nothing is lost.`;
}

export const PAYOUT_STEP_UP_COPY: StepUpCopyTable = {
  [PAYOUT_ACH_STEP_UP_ACTION]: {
    description: () =>
      'Givmo asks everyone to have signed in within the last 30 minutes before changing bank details. Your new details are saved as soon as you confirm.',
    consequence: 'Nothing is saved until you confirm.',
    challengeAction: 'save your new bank details',
  },
  [PAYOUT_CHECK_STEP_UP_ACTION]: {
    description: () => confirmBefore('before we change how your organization receives grants'),
    challengeAction: 'change how your organization receives grants',
  },
};

/**
 * The same words for the bank form opened after a change of ownership (CPA-BE69, D-CP-112): the
 * Global Admin may be entering the very account on file again, so the bank-details save drops "new",
 * as the form's "Enter your bank details" does. Ordinary changes keep {@link PAYOUT_STEP_UP_COPY}.
 */
export const PAYOUT_REENTRY_STEP_UP_COPY: StepUpCopyTable = {
  ...PAYOUT_STEP_UP_COPY,
  [PAYOUT_ACH_STEP_UP_ACTION]: {
    ...PAYOUT_STEP_UP_COPY[PAYOUT_ACH_STEP_UP_ACTION],
    description: () =>
      'Givmo asks everyone to have signed in within the last 30 minutes before changing bank details. Your details are saved as soon as you confirm.',
    challengeAction: 'save your bank details',
  },
};
